<template>

  <q-header class="bg-white text-dark" :class="{ 'initial': headerInitial }" :height-hint="0">

    <nav class="ssr-navigation" aria-label="Основная навигация" v-if="false">
      <ul>
        <li v-for="item in menuItemsSeo" :key="item.id!">
          <MenuItemLink :item="item">{{ item.label }}</MenuItemLink>
        </li>
      </ul>
    </nav>

    <q-resize-observer @resize="onResizeGlobal" :debounce="200"/>
    <q-scroll-observer @scroll="onScroll" :debounce="0" :scroll-target="scrollTarget"/>

    <div
        ref="desktopHeaderRef"
        class="c-desktop text-dark gt-md"
    >
      <q-resize-observer @resize="onResizeDesktop" :debounce="200"/>
      <HeaderDesktop :header-collapsed="headerCollapsed" @logo-click="handleLogoClick"/>
    </div>

    <div
        ref="mobileHeaderRef"
        class="c-mobile text-dark lt-lg"
    >
      <q-resize-observer @resize="onResizeMobile" :debounce="200"/>
      <div
          ref="mobileTopRef"
          class="c-mobile__top"
      >
        <q-resize-observer @resize="onResizeMobileTop" :debounce="200"/>
        <HeaderMobileTop @logo-click="handleLogoClick"/>
      </div>
      <div class="c-mobile__menu" v-if="!hideMobileMenu">
        <Menu
            mode="slider"
            class=""
            paddingX="16px"
            paddingY="2px"
            :isFirstScreen="isFirstScreen"
        />
      </div>
    </div>

  </q-header>


</template>

<script setup lang="ts">
import {useDebugComponent} from "@/core/composable/useDebugComponent";
import {useManualScroll} from "@/core/composable/useManualScroll";
import {useLayoutStore} from "@/stores/layout";
import {storeToRefs} from "pinia";
import {QResizeObserverProps, QScrollObserverProps, useQuasar} from "quasar";
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import HeaderMobileTop from "./HeaderMobileTop.vue";
import HeaderDesktop from "./HeaderDesktop.vue";
import Menu from "./Menu.vue";
import {useAdaptiveStore} from "@/stores/adaptive";
import {useMenuStore} from "@/modules/menu/store/menu";
import MenuItemLink from "@/components/Ui/Menu/MenuItemLink.vue";

const layoutStore = useLayoutStore()
const adaptiveStore = useAdaptiveStore()

const $q = useQuasar()
const {setHeaderHeight} = layoutStore
const {headerHeightCollapsed, headerTopHeight} = storeToRefs(layoutStore)

const {isMobileScreen} = storeToRefs(adaptiveStore)

const menuStore = useMenuStore();
const {menuItems} = storeToRefs(menuStore);


// SEO-friendly версия меню для SSR
const menuItemsSeo = computed(() => {
  return menuItems.value.top || [];
});

// Refs для элементов
const mobileHeaderRef = ref<HTMLElement | null>(null)
const mobileTopRef = ref<HTMLElement | null>(null)
const desktopHeaderRef = ref<HTMLElement | null>(null)
const scrollTarget = ref<Element | undefined>(undefined)

// ============================================
// КОНФИГУРАЦИЯ МОБИЛЬНОЙ ШАПКИ
// ============================================

// Режимы вычисления высоты мобильной шапки
type MobileHeaderHeightMode = 'dynamic' | 'static'
const MOBILE_HEADER_HEIGHT_MODE: MobileHeaderHeightMode = 'dynamic' as MobileHeaderHeightMode

// Статические значения высот для мобильной шапки (используются только в режиме 'static')
const STATIC_MOBILE_HEADER_FULL_HEIGHT = 114 // px - общая высота мобильной шапки
const STATIC_MOBILE_HEADER_TOP_HEIGHT = 59 // px - высота mobile-top

// ============================================
// КЕШИРОВАННЫЕ ЗНАЧЕНИЯ ВЫСОТ
// ============================================

// Мобильная версия
let cachedMobileFullHeight = 0 // Полная высота мобильной шапки (top + menu)
let cachedMobileTopHeight = 0 // Высота только верхней части (mobile-top)
let cachedMobileCollapsedHeight = 0 // Высота свёрнутой мобильной шапки (только menu)

// Десктопная версия
let cachedDesktopFullHeight = 0 // Полная высота десктопной шапки
let cachedDesktopCollapsedHeight = 0 // Высота свёрнутой десктопной шапки

// ============================================
// СОСТОЯНИЕ ШАПКИ
// ============================================

const headerCollapsed = ref(false) // Свёрнута ли шапка
const headerInitial = ref(true) // Initial режим - шапка static до mounted

/**
 * Флаг первого экрана.
 * true - пользователь находится в пределах первых двух экранов страницы.
 * Используется для изменения стилей меню на мобильной версии.
 *
 * @applies Преимущественно мобильная версия
 */
const isFirstScreen = ref(false)

let isCollapsed = false // Кешированное состояние для избежания лишних DOM операций

const route = useRoute()

// Computed для проверки нужно ли скрывать мобильное меню
const hideMobileMenu = computed(() => route.meta.hideMobileMenu === true)

// ============================================
// ОБРАБОТЧИКИ СОБЫТИЙ
// ============================================

/**
 * Обработчик клика по логотипу.
 * Если мы уже на главной странице - скроллим наверх плавно.
 * Иначе router-link сработает как обычно и перейдёт на главную.
 *
 * @applies Мобильная и десктопная версии
 */
const handleLogoClick = (event: MouseEvent) => {
  // Проверяем что мы уже на главной странице по имени роута
  if (route.name === 'front') {
    // Предотвращаем переход (мы уже на главной)
    event.preventDefault()
    // Плавный скролл наверх
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }
  // Если мы не на главной - router-link сработает как обычно
}

// ============================================
// КОНФИГУРАЦИЯ СКРОЛЛА (применяется к обеим версиям: мобильной и десктопной)
// ============================================

/**
 * Минимальное накопленное расстояние скролла (в px) для срабатывания сворачивания/разворачивания шапки.
 * Если пользователь проскроллил меньше этого значения, шапка не изменит своё состояние.
 * Это предотвращает случайные срабатывания при небольших движениях.
 *
 * @applies Мобильная и десктопная версии
 */
const SCROLL_THRESHOLD = 50

/**
 * Минимальная позиция скролла от верха страницы (в px), при которой шапка может быть свёрнута.
 * Если пользователь находится выше этой позиции, шапка всегда остаётся развёрнутой.
 * Это гарантирует что шапка видна в самом начале страницы.
 *
 * @applies Мобильная версия
 */
const MIN_SCROLL_POSITION = 100

/**
 * Порог позиции скролла для десктопной версии (в px).
 * Выше этой позиции (ближе к началу страницы) - шапка развёрнута.
 * Ниже этой позиции - шапка свёрнута.
 * Логика не зависит от направления скролла, только от абсолютной позиции.
 *
 * @applies Десктопная версия
 */
const DESKTOP_COLLAPSE_THRESHOLD = 700

/**
 * Расстояние "уверенного" скролла вверх (в px) для разворачивания свёрнутой шапки.
 * Когда шапка свёрнута, требуется проскроллить вверх на это расстояние чтобы она развернулась.
 * Это предотвращает случайное разворачивание при небольших движениях вверх.
 * Применяется только при ручном свайп-скролле (не при программном).
 *
 * @applies Преимущественно мобильная версия (для touch-событий), но работает и на десктопе
 */
const CONFIDENT_SCROLL_THRESHOLD = 700

/**
 * Минимальная высота страницы (в px) при которой разрешено сворачивание шапки.
 * Если страница короче этого значения, шапка всегда остаётся развёрнутой.
 * Это предотвращает сворачивание на коротких страницах где это не имеет смысла.
 *
 * @applies Мобильная и десктопная версии
 */
const MIN_PAGE_HEIGHT_FOR_COLLAPSE = 1000

// ============================================
// СОСТОЯНИЕ СКРОЛЛА (общее для обеих версий)
// ============================================

/**
 * Последняя зафиксированная позиция скролла (в px от верха страницы).
 * Используется для вычисления дельты (разницы) между текущей и предыдущей позицией.
 *
 * @applies Мобильная и десктопная версии
 */
let scrollLastPosition = 0

/**
 * Накопленная дельта скролла (в px) с момента последней смены направления.
 * Увеличивается при движении в одном направлении, сбрасывается при смене направления.
 * Используется для определения "уверенности" движения - достаточно ли пользователь проскроллил.
 *
 * @applies Мобильная и десктопная версии
 */
let scrollDelta = 0

/**
 * Позиция скролла (в px) где произошла последняя смена направления движения.
 * Используется для расчёта расстояния "уверенного" скролла вверх при разворачивании шапки.
 * Обновляется только при реальном пользовательском свайпе (не при программном скролле).
 *
 * @applies Преимущественно мобильная версия (для touch-событий), но работает и на десктопе
 */
let lastDirectionChangePosition = 0

/**
 * Флаг предыдущего направления скролла.
 * true = скроллили вниз, false = скроллили вверх.
 * Используется для определения момента смены направления движения.
 *
 * @applies Мобильная и десктопная версии
 */
let wasScrollingDown = false

// ============================================
// TOUCH СОБЫТИЯ (только для мобильных)
// ============================================

/**
 * Композабл для определения ручной прокрутки пользователя.
 * Отличает реальные свайпы от программных скроллов и простых тапов.
 *
 * @applies Преимущественно мобильная версия (touch-устройства)
 */
const {isManualScrolling} = useManualScroll(10)

// ============================================
// LIFECYCLE HOOKS
// ============================================

onMounted(() => {
  // Удаляем initial класс после монтирования
  headerInitial.value = false
})

// ============================================
// ПРИМЕНЕНИЕ СОСТОЯНИЯ COLLAPSED (применяется к обеим версиям)
// ============================================

/**
 * Флаг ожидающего requestAnimationFrame для обновления состояния collapsed.
 * true - RAF уже запланирован, новые вызовы будут объединены в один.
 * false - можно планировать новый RAF.
 * Используется для батчинга множественных обновлений в один кадр анимации.
 */
let isCollapsedUpdateScheduled = false

/**
 * Ожидающее состояние collapsed для применения в следующем RAF.
 * null - нет ожидающих изменений.
 * boolean - новое состояние которое нужно применить.
 */
let pendingCollapsedState: boolean | null = null

/**
 * Применяет состояние collapsed к шапке с использованием GPU-ускорения.
 * Использует transform + height для плавной анимации.
 *
 * Работает с обеими версиями шапки:
 * - Мобильная: применяет класс --collapsed к .c-mobile, сдвигает вверх через transform
 * - Десктопная: применяет класс --collapsed к .c-desktop, изменяет размеры логотипа
 *
 * Обновляет headerHeight в зависимости от активной версии (isMobileScreen.value).
 *
 * @applies Мобильная и десктопная версии
 */
const applyCollapsedState = () => {
  isCollapsedUpdateScheduled = false

  if (pendingCollapsedState === null) {
    return
  }

  const shouldCollapse = pendingCollapsedState
  pendingCollapsedState = null

  if (shouldCollapse !== isCollapsed) {
    isCollapsed = shouldCollapse

    // Применяем класс только к активной версии шапки
    if (isMobileScreen.value) {
      if (mobileHeaderRef.value) {
        mobileHeaderRef.value.classList.toggle('--collapsed', shouldCollapse)
      }
    } else {
      if (desktopHeaderRef.value) {
        desktopHeaderRef.value.classList.toggle('--collapsed', shouldCollapse)
      }
    }

    // Обновляем headerHeight для Section
    if (headerCollapsed.value !== shouldCollapse) {
      headerCollapsed.value = shouldCollapse

      if (shouldCollapse) {
        // Свёрнутое состояние
        if (isMobileScreen.value) {
          // Мобильная: высота = полная высота - высота верхней части
          setHeaderHeight(cachedMobileFullHeight - cachedMobileTopHeight)
        } else {
          // Десктопная: используем кешированную collapsed высоту
          setHeaderHeight(cachedDesktopCollapsedHeight)
        }
      } else {
        // Развёрнутое состояние
        if (isMobileScreen.value) {
          setHeaderHeight(cachedMobileFullHeight)
        } else {
          setHeaderHeight(cachedDesktopFullHeight)
        }
      }
    }
  }
}

/**
 * Планирует обновление состояния collapsed через requestAnimationFrame.
 * Батчит множественные вызовы в один RAF для оптимизации производительности.
 *
 * Применяется к обеим версиям шапки (мобильной и десктопной), но логика
 * сворачивания/разворачивания более актуальна для мобильной версии где
 * используется transform для скрытия верхней части.
 *
 * @param shouldCollapse - Новое состояние (true = свернуть, false = развернуть)
 * @applies Мобильная и десктопная версии (преимущественно мобильная)
 */
const scheduleCollapsedUpdate = (shouldCollapse: boolean) => {
  pendingCollapsedState = shouldCollapse

  if (!isCollapsedUpdateScheduled) {
    isCollapsedUpdateScheduled = true
    requestAnimationFrame(applyCollapsedState)
  }
}

// ============================================
// ОБРАБОТЧИКИ RESIZE
// ============================================

const onResizeGlobal: QResizeObserverProps['onResize'] = (info) => {
  // Общий обработчик - обновляем headerHeight только если не свернуто
  if (!isCollapsed) {
    setHeaderHeight(info.height)
  }
}

// ДЕСКТОПНАЯ ВЕРСИЯ
const onResizeDesktop: QResizeObserverProps['onResize'] = (info) => {
  const newHeight = info.height

  if (isCollapsed) {
    // Если шапка свернута, сохраняем как collapsed высоту
    cachedDesktopCollapsedHeight = newHeight
    headerHeightCollapsed.value = newHeight
    setHeaderHeight(newHeight)
  } else {
    // Если шапка развернута, сохраняем как полную высоту
    cachedDesktopFullHeight = newHeight
    setHeaderHeight(newHeight)
  }
}

// МОБИЛЬНАЯ ВЕРСИЯ - полная высота (top + menu)
const onResizeMobile: QResizeObserverProps['onResize'] = (info) => {
  // В статическом режиме используем фиксированные значения
  if (MOBILE_HEADER_HEIGHT_MODE === 'static') {
    const newHeight = STATIC_MOBILE_HEADER_FULL_HEIGHT

    if (!isCollapsed) {
      setHeaderHeight(newHeight)
      cachedMobileFullHeight = newHeight
    }

    if (isCollapsed) {
      cachedMobileCollapsedHeight = newHeight - STATIC_MOBILE_HEADER_TOP_HEIGHT
      headerHeightCollapsed.value = cachedMobileCollapsedHeight
    }

    // Устанавливаем CSS переменные
    if (mobileHeaderRef.value) {
      mobileHeaderRef.value.style.setProperty('--mobile-full-height', `${newHeight}px`)
    }

    return
  }

  // Динамический режим - измеряем контейнер
  const newHeight = info.height

  // НЕ обновляем headerHeight если шапка свернута
  // потому что CSS height изменяется и дает неправильное значение
  if (!isCollapsed) {
    setHeaderHeight(newHeight)
    cachedMobileFullHeight = newHeight

    // Сохраняем полную высоту для расчета уменьшенной высоты
    if (mobileHeaderRef.value) {
      mobileHeaderRef.value.style.setProperty('--mobile-full-height', `${newHeight}px`)
    }
  }

  // Если шапка свернута, сохраняем это как collapsed высоту
  if (isCollapsed) {
    cachedMobileCollapsedHeight = newHeight
    headerHeightCollapsed.value = newHeight
  }
}

// МОБИЛЬНАЯ ВЕРСИЯ - только верхняя часть (mobile-top)
const onResizeMobileTop: QResizeObserverProps['onResize'] = (info) => {
  // В статическом режиме используем фиксированные значения
  if (MOBILE_HEADER_HEIGHT_MODE === 'static') {
    const newHeight = STATIC_MOBILE_HEADER_TOP_HEIGHT
    headerTopHeight.value = newHeight
    cachedMobileTopHeight = newHeight

    // Устанавливаем высоту верхней части для расчетов
    if (mobileHeaderRef.value) {
      mobileHeaderRef.value.style.setProperty('--mobile-top-height', `${newHeight}px`)
      mobileHeaderRef.value.style.setProperty('--mobile-full-height', `${STATIC_MOBILE_HEADER_FULL_HEIGHT}px`)
    }

    return
  }

  // Динамический режим - измеряем контейнер
  const newHeight = info.height
  headerTopHeight.value = newHeight
  cachedMobileTopHeight = newHeight

  // Устанавливаем высоту верхней части для расчетов
  if (mobileHeaderRef.value) {
    mobileHeaderRef.value.style.setProperty('--mobile-top-height', `${newHeight}px`)

    // ВАЖНО: также обновляем полную высоту при изменении верхней части
    const fullHeight = mobileHeaderRef.value.offsetHeight
    cachedMobileFullHeight = fullHeight
    mobileHeaderRef.value.style.setProperty('--mobile-full-height', `${fullHeight}px`)
  }
}

// ============================================
// ОБРАБОТЧИК СКРОЛЛА (применяется к обеим версиям)
// ============================================

/**
 * Обработчик события скролла страницы.
 * Управляет сворачиванием/разворачиванием шапки в зависимости от направления и интенсивности скролла.
 *
 * Логика работает одинаково для обеих версий (мобильной и десктопной),
 * но некоторые проверки более актуальны для мобильной версии (touch-события).
 *
 * @applies Мобильная и десктопная версии
 */
const onScroll: QScrollObserverProps['onScroll'] = (info) => {
  // ========================================
  // ОБЩИЕ ПРОВЕРКИ (обе версии)
  // ========================================

  // Early return для заблокированного скролла (модальные окна, drawer и т.д.)
  if (document.body.classList.contains('q-body--prevent-scroll')) {
    return;
  }

  // Не сворачиваем шапку если высота страницы меньше минимальной
  // Применяется к обеим версиям
  const pageHeight = document.documentElement.scrollHeight
  if (pageHeight < MIN_PAGE_HEIGHT_FOR_COLLAPSE) {
    // Если страница короткая и шапка свернута - принудительно разворачиваем
    if (isCollapsed) {
      scheduleCollapsedUpdate(false)
    }
    return;
  }

  // ========================================
  // ВЫЧИСЛЕНИЕ ПОЗИЦИИ И НАПРАВЛЕНИЯ (обе версии)
  // ========================================

  const currentPosition = info.position.top
  const windowHeight = window.innerHeight

  // Обновляем флаг первого экрана (используется в мобильном меню)
  isFirstScreen.value = currentPosition < windowHeight * 2

  const delta = currentPosition - scrollLastPosition

  // Оптимизация: пропускаем микро-движения
  if (Math.abs(delta) < 1) {
    return
  }

  // Определяем направление скролла
  const isScrollingDown = delta > 0
  const isScrollingUp = delta < 0

  // ========================================
  // ОТСЛЕЖИВАНИЕ СМЕНЫ НАПРАВЛЕНИЯ (только для мобильной версии)
  // ========================================

  // Отслеживаем смену направления только для мобильной версии
  // На десктопе используется простая логика на основе позиции, направление не важно
  if (isMobileScreen.value) {
    // Учитываем только при реальном пользовательском свайпе (touch-события)
    if (isManualScrolling.value) {
      if ((isScrollingDown && !wasScrollingDown) || (isScrollingUp && wasScrollingDown)) {
        // Направление изменилось - запоминаем позицию и сбрасываем счетчик
        lastDirectionChangePosition = currentPosition
        scrollDelta = 0
        wasScrollingDown = isScrollingDown
      } else {
        // Направление не изменилось - накапливаем дельту
        scrollDelta += Math.abs(delta)
      }
    } else {
      // Программный скролл на мобильной версии — не учитываем его в "уверенной" дистанции
      scrollDelta = 0
    }
  }
  // На десктопе не отслеживаем направление - не нужно

  scrollLastPosition = currentPosition

  // ========================================
  // ЛОГИКА ПОКАЗА/СКРЫТИЯ ШАПКИ
  // ========================================

  let shouldCollapse: boolean

  if (isMobileScreen.value) {
    // ========================================
    // МОБИЛЬНАЯ ВЕРСИЯ: логика на основе направления и дельты скролла
    // ========================================

    if (currentPosition < MIN_SCROLL_POSITION) {
      // В самом верху страницы - всегда показываем
      shouldCollapse = false

    } else if (isScrollingDown && scrollDelta > SCROLL_THRESHOLD) {
      // Скроллим вниз уверенно - скрываем
      shouldCollapse = true

    } else if (isScrollingUp && scrollDelta > SCROLL_THRESHOLD) {
      // Скроллим вверх - проверяем уверенность

      if (isCollapsed) {
        // Если шапка скрыта, требуем уверенный скролл для разворачивания
        if (isManualScrolling.value) {
          const scrolledUpDistance = Math.abs(currentPosition - lastDirectionChangePosition)
          shouldCollapse = scrolledUpDistance < CONFIDENT_SCROLL_THRESHOLD
        } else {
          // Программный скролл — удерживаем текущее свернутое состояние
          shouldCollapse = true
        }
      } else {
        // Если шапка видима, обычная логика - сразу разворачиваем
        shouldCollapse = false
      }

    } else {
      // Недостаточно движения - сохраняем текущее состояние
      shouldCollapse = isCollapsed
    }

  } else {
    // ========================================
    // ДЕСКТОПНАЯ ВЕРСИЯ: простая логика на основе позиции скролла
    // ========================================

    // Выше порога (ближе к началу) - развёрнута
    // Ниже порога - свёрнута
    shouldCollapse = currentPosition > DESKTOP_COLLAPSE_THRESHOLD
  }

  // ========================================
  // ПРИМЕНЕНИЕ ИЗМЕНЕНИЙ (обе версии)
  // ========================================

  // Батчим DOM операции через RAF для оптимизации
  if (shouldCollapse !== isCollapsed) {
    scheduleCollapsedUpdate(shouldCollapse)
  }
}

// ============================================
// WATCHERS
// ============================================

// Watcher для обновления кешированных значений при изменении состояния collapsed
watch(headerCollapsed, async (newCollapsed: boolean) => {
  await nextTick()

  // Принудительно обновляем высоты после изменения CSS классов
  // Обрабатываем только активную версию шапки
  if (isMobileScreen.value) {
    // Мобильная версия
    if (mobileHeaderRef.value) {
      const currentHeight = mobileHeaderRef.value.offsetHeight
      if (newCollapsed) {
        cachedMobileCollapsedHeight = currentHeight
      } else {
        cachedMobileFullHeight = currentHeight
      }
    }
  } else {
    // Десктопная версия
    if (desktopHeaderRef.value) {
      const currentHeight = desktopHeaderRef.value.offsetHeight
      if (newCollapsed) {
        cachedDesktopCollapsedHeight = currentHeight
      } else {
        cachedDesktopFullHeight = currentHeight
      }
    }
  }
}, {flush: 'post'})

// Watcher для проверки высоты страницы при смене маршрута
watch(() => route.path, async () => {
  await nextTick()

  // Даем время на рендер новой страницы
  setTimeout(() => {
    const pageHeight = document.documentElement.scrollHeight
    if ((pageHeight < MIN_PAGE_HEIGHT_FOR_COLLAPSE) && isCollapsed) {
      // Если новая страница короткая и шапка свернута - разворачиваем
      scheduleCollapsedUpdate(false)
    }
  }, 100)
}, {flush: 'post'})

// Watcher для пересчета высоты мобильной шапки при изменении видимости меню
watch(hideMobileMenu, async () => {
  if (!isMobileScreen.value) {
    return;
  }

  await nextTick()

  // Даем время на изменение DOM
  setTimeout(() => {
    if (mobileHeaderRef.value) {
      // В статическом режиме используем фиксированные значения
      if (MOBILE_HEADER_HEIGHT_MODE === 'static') {
        const newHeight = STATIC_MOBILE_HEADER_FULL_HEIGHT
        cachedMobileFullHeight = newHeight

        if (!isCollapsed) {
          setHeaderHeight(newHeight)
        }

        mobileHeaderRef.value.style.setProperty('--mobile-full-height', `${newHeight}px`)
      } else {
        // Динамический режим - измеряем контейнер
        const newHeight = mobileHeaderRef.value.offsetHeight
        cachedMobileFullHeight = newHeight

        // Обновляем headerHeight только если шапка не свернута
        if (!isCollapsed) {
          setHeaderHeight(newHeight)
        }

        // Обновляем CSS переменные
        mobileHeaderRef.value.style.setProperty('--mobile-full-height', `${newHeight}px`)
      }
    }
  }, 50)
}, {flush: 'post'})

// ============================================
// DEBUG
// ============================================

// Debug композабл для отслеживания ререндеров
useDebugComponent('Header', {collectRenders: true});

</script>

<style lang="scss" scoped>

// Initial режим - шапка static до mounted (переопределяет fixed из Quasar)
.q-header.initial {
  position: static !important;
}

.c-mobile {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;

  // CSS Containment для изоляции layout/paint
  contain: layout style;

  // ИДЕАЛЬНОЕ РЕШЕНИЕ: transform + height
  transform: translate3d(0, 0, 0);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
  height 0.1s cubic-bezier(0.4, 0, 0.2, 1);

  // Оптимизация для GPU
  will-change: transform, height;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;

  .c-mobile__top {
    position: relative;
    border-bottom: 1px solid #EEEEEE;
    height: fit-content;
    background-color: #FBFBFB;
    z-index: 1;

    // Изолируем paint операции
    contain: paint;
  }

  &.--collapsed {
    // ИДЕАЛЬНАЯ КОМБИНАЦИЯ: сдвигаем вверх + уменьшаем высоту
    transform: translate3d(0, calc(-1 * var(--mobile-top-height, 0px)), 0);
    height: calc(var(--mobile-full-height, auto) - var(--mobile-top-height, 0px));

    // Дополнительные стили для свернутого состояния
    .c-mobile__top {
      overflow: hidden;
      box-shadow: none;
      pointer-events: none;
    }
  }
}


.c-mobile__menu {
  border-bottom: 1px solid #DDDDDD;
  background-color: #FFFFFF;
  z-index: 2;
  position: relative;
  padding: 4px 0 4px 0;

  :deep(.adv-menu__item) {
    padding: 12px 10px;
  }
}

.c-mobile__menu__item {

  padding: 0 10px;

  :deep() {
    .q-tab__content {
      padding: 9px 0 6px 0;
    }

    .q-tab__label {
      font-size: 15px;
    }
  }

  &:not(.q-tab--inactive) {
    :deep() {
      .q-tab__label {
        background-color: $primary;
        border-radius: 10px;
        padding: 0 10px;
        color: #FFFFFF;
      }
    }
  }
}

.c-desktop {
  background-color: #fff;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.--collapsed {
    // Класс для свёрнутого состояния (применяется к контейнеру)
  }
}

.icons {
  .icons__item {
    display: inline-block;
    text-decoration: none;
  }
}

.scrollup {
  position: fixed;
  z-index: 10;
  bottom: 30px;
  right: 30px;
}


</style>
